// AI-CONTEXT: See docs/features/datakwaliteitsscore.md
import { useHistory } from 'react-router-dom'
import useDataQualityScore from '../../hooks/useDataQualityScore'
import { dataQualityBand, DIMENSION_LABELS, INDICATOR_LABELS, formatScoreGrowth } from '../../helpers/dashboard/dataQualityDisplay'
import PageHeader from '../../components/layout/PageHeader'
import { client } from '../../hooks/Client'

const INDICATORS_PER_DIMENSION = {
    dekking: ['D1', 'D2', 'D3', 'D4'],
    volledigheid: ['V1', 'V2', 'V3', 'V4'],
    integriteit: [],
    actualiteit: ['A1', 'A2', 'A3'],
}

/**
 * @param {object|null|undefined} value - Firestore Timestamp (of serialisatie via de callable)
 * @returns {Date|null}
 */
const toDate = (value) => {
    if (!value) return null
    if (typeof value.toDate === 'function') return value.toDate()
    const seconds = value._seconds ?? value.seconds
    return typeof seconds === 'number' ? new Date(seconds * 1000) : null
}

/**
 * Detailpagina achter de datakwaliteit-KPI-kaart: de bevroren score met de
 * volledige uitsplitsing per dimensie en indicator — de verbeteragenda van de
 * organisatie. Toont daarnaast een live "actuele stand" (voorlopig gelabeld).
 */
const DataQualityDetail = () => {
    const history = useHistory()
    const { stored, live, growth, loading, error } = useDataQualityScore({ live: true })
    const growthLine = formatScoreGrowth(growth)

    const openDimension = (dimension) => history.push(`/${client}/datakwaliteit/${dimension}`)

    const score = typeof stored?.Score === 'number' ? stored.Score : null
    const band = dataQualityBand(score)
    const computedAt = toDate(stored?.ComputedAt)
    const breakdown = stored?.Breakdown

    return (
        <div className='main'>
            <div className='main-container'>
                <PageHeader
                    heading='Datakwaliteit'
                    description='Hoe volledig, consistent en actueel zijn de eigen metingen? De score beoordeelt uitsluitend datakwaliteit — niet de hoogte van de impact.'
                />

                {error ? (
                    <p className='data-quality-empty'>
                        De score kon niet worden geladen. Vernieuw de pagina of probeer het later opnieuw.
                    </p>
                ) : score === null ? (
                    <p className='data-quality-empty'>
                        {loading
                            ? 'Score wordt geladen…'
                            : 'Nog geen berekende score — de eerste nachtelijke berekening moet nog draaien.'}
                    </p>
                ) : (
                    <>
                        <div className='data-quality-hero'>
                            <p className={`data-quality-hero-score data-quality-score--${band}`}>{score}</p>
                            <div className='data-quality-bar data-quality-bar--wide'>
                                <div
                                    className={`data-quality-bar-fill data-quality-bar-fill--${band}`}
                                    style={{ width: `${score}%` }}
                                />
                            </div>
                            {growthLine && (
                                <p className={`data-quality-growth data-quality-growth--${growthLine.direction}`}>{growthLine.text}</p>
                            )}
                            <p className='data-quality-meta'>
                                {computedAt && `Berekend op ${computedAt.toLocaleDateString('nl-NL')} · `}
                                Meetstandaard {stored.MeetstandaardVersion}
                                {' · '}Meetprofiel: {stored.Profile === 'anonymous' ? 'anoniem' : 'gekoppeld'}
                            </p>
                            {stored.Provisional && (
                                <p className='data-quality-provisional'>
                                    Score voorlopig — deze organisatie heeft minder dan 6 maanden data.
                                    De score dient in deze fase uitsluitend als verbeterinstrument.
                                </p>
                            )}
                            {typeof live?.score === 'number' && live.score !== score && (
                                <p className='data-quality-meta'>
                                    Actuele stand (voorlopig, nog niet bevroren): {live.score}
                                </p>
                            )}
                        </div>

                        {breakdown && (
                            <div className='data-quality-dimensions'>
                                {Object.keys(INDICATORS_PER_DIMENSION).map((dimension) => {
                                    const details = breakdown[dimension]
                                    if (!details) return null
                                    const dimensionScore = Math.round(details.score)
                                    return (
                                        <div
                                            key={dimension}
                                            className='data-quality-dimension data-quality-dimension--clickable'
                                            role='button'
                                            tabIndex={0}
                                            onClick={() => openDimension(dimension)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault()
                                                    openDimension(dimension)
                                                }
                                            }}
                                        >
                                            <div className='data-quality-dimension-header'>
                                                <h3>{DIMENSION_LABELS[dimension]}</h3>
                                                <span className='data-quality-dimension-weight'>
                                                    weegt {Math.round(details.weight * 100)}%
                                                </span>
                                                <span className={`data-quality-dimension-score data-quality-score--${dataQualityBand(dimensionScore)}`}>
                                                    {dimensionScore}
                                                </span>
                                            </div>
                                            {dimension === 'integriteit' ? (
                                                <p className='data-quality-indicator'>
                                                    Foutvrije records: {details.totalRecords - details.faultyRecords} van {details.totalRecords}
                                                    {details.faultyRecords > 0 && ` — ${details.faultyRecords} met een geconstateerde fout`}
                                                </p>
                                            ) : (
                                                INDICATORS_PER_DIMENSION[dimension].map((code) => {
                                                    const value = details[code]
                                                    return (
                                                        <p key={code} className='data-quality-indicator'>
                                                            <span className='data-quality-indicator-code'>{code}</span>
                                                            {INDICATOR_LABELS[code]}:{' '}
                                                            {value === null || value === undefined
                                                                ? 'niet berekenbaar — gewicht herverdeeld'
                                                                : `${Math.round(value)} op 100`}
                                                        </p>
                                                    )
                                                })
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}

export default DataQualityDetail
